import {ObjUtil, Util} from 'yougile-util'
import {
  ChatJSON,
  DataObjectId,
  Hub,
  SandboxTree,
  Tag,
  TagJSON
} from 'yougile-model'
import {Board} from './board'
import {Column} from './column'
import {Project} from './project'
import {Sticker} from './sticker/sticker'
import {SubtaskUtil} from './subtask-util'

export interface ProjectData {
  title: string
  stickers: StickerData[]
  boards: BoardData[]
  tasks: Tasks
}

export interface TaskLocalData {
  title: string
  timestamp: number
  description: string
  data: Hub['data']
  complete: Hub['complete']
  chat: ChatJSON
  stickers: Hub['stickers']
  subtasks: DataObjectId[]
}

export interface ColumnData {
  title: string
  color: string
  tasks: string[]
}
export interface BoardData {
  title: string
  stickers: Hub['stickers']
  columns: ColumnData[]
}

interface StickerData {
  id: DataObjectId
  title: string
  states: TagJSON['states']
  properties: TagJSON['properties']
}

interface Tasks {
  [id: string]: TaskLocalData
}

export const projectImportToJson = (
  box: SandboxTree,
  project: Hub
): ProjectData => {
  const stickerIds: Set<DataObjectId> = new Set()
  const tasks: {[id: DataObjectId]: TaskLocalData} = {}
  const boardHubs = Project.listBoards(project, true)

  const mapTasks = (t: Hub): DataObjectId => {
    ObjUtil.forEach(t.stickers, (id: DataObjectId) => stickerIds.add(id))

    if (!tasks[t.id]) {
      const task = taskLocalDataFromHub(box, t)
      tasks[t.id] = task
      if (t.data.subtaskList) {
        task.subtasks = SubtaskUtil.getSubtasksFromListAsHub(
          t.data.subtaskList
        ).map(mapTasks)
      }
    }

    return t.id
  }

  const mapColumns = (c: Hub): ColumnData => {
    return {
      title: c.title,
      color: c.description,
      tasks: Column.listTasks(c, false, {sorted: true}).map(mapTasks)
    }
  }
  const mapBoards = (b: Hub): BoardData => {
    return {
      title: b.title,
      stickers: b.stickers,
      columns: Board.listColumns(b, {sorted: true}).map(mapColumns)
    }
  }
  const boards = boardHubs.map(mapBoards)

  const stickers: StickerData[] = []
  stickerIds.forEach(id => {
    const tag = box.get(id) as Tag
    if (tag) {
      const type = Sticker.getType(tag)
      if (type === 'string' || type === 'sprint') {
        stickers.push({
          id,
          title: tag.title,
          states: tag.states,
          properties: tag.properties
        })
      }
    }
  })

  return {title: project.title, stickers, boards, tasks}
}

const taskLocalDataFromHub = (_box: SandboxTree, task: Hub): TaskLocalData => {
  return Util.clone({
    title: task.title,
    timestamp: task.timestamp,
    description: task.description,
    data: ObjUtil.omit(task.data, 'subtaskList'),
    complete: task.complete,
    chat: task.chat.toJSON(),
    stickers: task.stickers,
    subtasks: []
  })
}
